import React, { useState } from 'react';
import { toast } from 'sonner';
import { PopulatedLead } from '../types';
import { leadsService } from '../services/leadsService';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
/**
 * Props do DeleteLeadDialog
 */
export interface DeleteLeadDialogProps {
  /** Se o modal está aberto */
  isOpen: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** Lead a ser excluído */
  lead: PopulatedLead | null;
  /** Callback após exclusão bem-sucedida */
  onSuccess?: () => void;
}

/**
 * Dialog de Confirmação de Exclusão de Lead
 *
 * Chrome = Modal shared (F72: portal, bottom-sheet mobile, always-light).
 * Exibe informações do lead e alerta sobre ação irreversível.
 *
 * @example
 * ```tsx
 * <DeleteLeadDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   lead={leadToDelete}
 *   onSuccess={() => refetch()}
 * />
 * ```
 */
export const DeleteLeadDialog: React.FC<DeleteLeadDialogProps> = ({
  isOpen,
  onClose,
  lead,
  onSuccess
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !lead) return null;

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await leadsService.deleteLead(lead.id);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      toast.error('Erro ao excluir lead. Tente novamente.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <ModalHeader onClose={onClose}>
        <div
          className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
          style={{ backgroundColor: '#fef2f2' }}
        >
          <Icon name="alert-triangle" className="w-5 h-5" style={{ color: '#DC2626' }} />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
          Excluir Lead
        </h2>
      </ModalHeader>

      <ModalContent>
        <p className="mb-4" style={{ color: '#1e293b' }}>
          Tem certeza que deseja excluir o lead de <strong>{lead.contact?.name || 'Sem nome'}</strong>?
        </p>

        {/* Lead Info Card */}
        <div
          className="p-3 rounded-lg mb-4"
          style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb'
          }}
        >
          <div className="space-y-2 text-sm">
            {lead.contact?.email && (
              <div style={{ color: '#64748b' }}>
                <strong style={{ color: '#1e293b' }}>Email:</strong> {lead.contact.email}
              </div>
            )}
            {lead.contact?.phone && (
              <div style={{ color: '#64748b' }}>
                <strong style={{ color: '#1e293b' }}>Telefone:</strong> {lead.contact.phone}
              </div>
            )}
            {lead.description && (
              <div style={{ color: '#64748b' }}>
                <strong style={{ color: '#1e293b' }}>Descrição:</strong> {lead.description}
              </div>
            )}
          </div>
        </div>

        {/* Warning */}
        <div
          className="p-3 rounded-lg flex items-start gap-3"
          style={{
            backgroundColor: '#FEE2E2',
            border: '1px solid #FECACA'
          }}
        >
          <Icon name="alert-triangle" className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#991B1B' }}>
              Atenção: Esta ação não pode ser desfeita!
            </p>
            <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
              Todas as informações relacionadas a este lead serão permanentemente removidas.
            </p>
          </div>
        </div>
      </ModalContent>

      <ModalFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isDeleting}
          data-testid="delete-lead-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="error"
          icon="trash"
          loading={isDeleting}
          onClick={handleDelete}
          disabled={isDeleting}
          data-testid="delete-lead-confirm"
        >
          {isDeleting ? 'Excluindo...' : 'Excluir Lead'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
